
/**
 * Module dependencies.
 */

import { ifProp, prop } from 'styled-tools';
import React, { ReactElement, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  color?: string,
  icon: string,
  size: string | unknown
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.span`
  display: inline-block;
  line-height: 0;
  max-width: ${prop('size')};
  position: relative;
  transform: translate3d(0, 0, 0);
  width: ${prop('size')};

  ${ifProp('color', css`
    color: ${prop('color')};`
  )}
`;

/**
 * Counter for unique SVG IDs.
 */

let svgIdCounter = 0;

/**
 * `Svg` component.
 */

const Svg = ({ icon, ...rest }: Props): ReactElement => {
  const idRef = useRef(svgIdCounter++);
  const scopedHtml = useMemo(() => {
    const suffix = `_${idRef.current}`;

    return icon
      .replace(/\bid="([^"]+)"/g, (_, id) => `id="${id}${suffix}"`)
      .replace(/url\(#([^)]+)\)/g, (_, id) => `url(#${id}${suffix})`)
      .replace(/href="#([^"]+)"/g, (_, id) => `href="#${id}${suffix}"`);
  }, [icon]);

  const innerHtml = {
    __html: scopedHtml // eslint-disable-line id-match
  };

  return (
    <Wrapper
      {...rest}
      dangerouslySetInnerHTML={innerHtml}
    />
  );
};

/**
 * Export `Svg` component.
 */

export default Svg;
